import { z } from 'zod'
import { Rule } from './rule.js'
import { Field } from './field.js'
import { Block, DefaultFieldId, DefaultFieldKey, FieldId } from './id.js'
import { Moment, Person } from './moment.js'
import { Text, TextDraft } from './text.js'

/**
 * Een standaardveld: een controltype met zijn eigenschappen al ingevuld (D-48, T10).
 *
 * Naast de bibliotheek van veldtypes staat een bibliotheek van standaardvelden, en een formulier
 * wordt opgebouwd door daaruit te combineren (D-50). Wat NWO daar zelf beheert zijn label,
 * hulptekst, verplichting en validatie, zonder de leverancier (D-49).
 *
 * **Het gaat als kopie de definitie in en nooit als verwijzing, en dat is D-51.** Die eis zegt
 * dat een gewijzigd standaardveld niets verandert aan formulieren die er eerder uit zijn gemaakt.
 * Een verwijzing zou precies dat wél doen: dan verandert de vorm van een bestaand concept doordat
 * iemand elders iets aanpast, en bij een gepubliceerde definitie zou het zelfs de bevriezing van
 * R-99 opheffen. Dus: bij het plaatsen wordt het veld gekopieerd, met een nieuw `VeldId` uit
 * `nieuwId()`, en daarna leidt de definitie zijn eigen leven.
 *
 * **En de kopie draagt een herkomstmerk, want dat is D-52.** Van een standaardveld moet te zien
 * zijn in welke formulieren het wordt gebruikt, zodat beheer een zichtbaar gevolg heeft. Een
 * zuivere kopie verliest dat, dus het geplaatste veld houdt `uitStandaardveld` bij: het id en de
 * versie waaruit het is gekopieerd. Dat is de spanning tussen D-51 en D-52, en dit is de enige
 * vorm waarin ze beide kloppen.
 *
 * **Het merk is herkomst en nooit een tweede waarheid.** Niets leidt de vorm van een veld uit dat
 * merk af, en niets werkt een geplaatst veld bij als de bibliotheek wijzigt; er staat een test op
 * dat het merk inert is. Zou het één keer worden gelezen om iets te bepalen, dan is er een tweede
 * definitie van hetzelfde begrip (repo-afspraak) en is D-51 alsnog gebroken.
 */
const Shape = z.strictObject({
  id: DefaultFieldId,
  /**
   * De vaste sleutel, zoals `titel-voorstel` (besluit van 1 november 2026).
   *
   * Gaat bij het plaatsen mee in het merk, zodat over formulieren en rondes heen te zien is dat twee
   * velden dezelfde vraag zijn, ook al heeft elk zijn eigen veld-id. Hij verandert nooit: een nieuwe
   * versie houdt hem, en de store weigert hem te wijzigen.
   */
  sleutel: DefaultFieldKey,
  /**
   * Hoe het standaardveld in de bibliotheek heet.
   *
   * Los van het label van het veld zelf, want die twee dienen iets anders: dit is wat de
   * inrichter in de bibliotheek zoekt ("Projecttitel, kort"), het label is wat de invuller op zijn
   * formulier ziet. Ze mogen gelijk zijn en hoeven het niet.
   */
  naam: Text,
  /**
   * De versie van dit standaardveld, oplopend per wijziging.
   *
   * Nodig voor D-52 in beide richtingen: welke formulieren gebruiken dit veld, en welke gebruiken
   * een oudere vorm ervan. Zonder versie is een geplaatste kopie niet te onderscheiden van een
   * kopie van vóór de laatste wijziging, en dan is het zichtbare gevolg van beheer een lijst
   * zonder betekenis. Een geheel getal en geen tijdstempel: het is een teller en geen moment.
   */
  versie: z.number().int().min(1),
  /**
   * De veldvorm zelf, zoals hij in een definitie komt te staan.
   *
   * Een volledige `Veld` en geen eigen halve vorm: er is nergens een tweede definitie van
   * hetzelfde begrip (repo-afspraak), en wat hier valideert is precies wat een definitie aanneemt.
   * Het `id` erin is dat van het standaardveld in de bibliotheek; bij het plaatsen krijgt de kopie
   * een nieuw `VeldId`, want anders staat hetzelfde id in twee formulieren.
   */
  veld: Field,
  /**
   * De regels die bij dit veld horen: verplichting en validatie (D-48).
   *
   * **Waarom deze hier staan, en waarom ze er bij M6.5 nog niet stonden.** D-48 zegt dat een
   * standaardveld een type is met de eigenschappen al ingevuld, "zoals label, hulptekst,
   * verplicht en validatie". De eerste twee staan op het veld, maar verplicht en validatie zijn
   * in dit model geen eigenschappen van een veld: het zijn `Regel`-objecten met `doelVeld`, en
   * die staan op de sectie (`regel.ts`, ontwerp 5.4). Een standaardveld dat alleen een `Veld`
   * draagt kan dus niet wat de eis vraagt, en dat is bij M6.10 rechtgezet. Het is geen vondst
   * van een leesronde maar van het teruglezen van de eistekst; twee ronden hebben er overheen
   * gekeken.
   *
   * **Hun `doelVeld` wijst naar het veld hierboven.** Bij het plaatsen krijgt de kopie een nieuw
   * `VeldId` en gaan de regels mee met dat nieuwe id, elk met een eigen nieuw `RuleId`. Zonder
   * die omzetting zou een geplaatste regel naar het bibliotheekveld wijzen en dus naar niets in
   * de definitie waar hij in staat.
   *
   * Leeg is normaal: een standaardveld zonder verplichting en zonder validatie is een geldig
   * standaardveld, en dat is de eenvoudigste vorm.
   */
  regels: z.array(Rule),
  /**
   * Een advies voor het compartiment bij het plaatsen (D-69, D-70, besluit van 1 november 2026).
   *
   * `true` is "hoort bij het deel dat doorgaat naar het volgende blok", `false` "blijft bij dit blok".
   * Een standaardveld hoort bij geen bouwblok en kent dus geen compartiment; welke er zijn staat in het
   * contract van het blok waarin het wordt geplaatst. Het advies wordt naast de keuze getoond en
   * kiest niets: D-69 kent geen standaardcompartiment, en een voorgevulde keuze zou een besluit zijn
   * dat niemand nam. Ontbreekt het, dan is er geen advies.
   */
  adviesGeeftDoor: z.boolean().optional(),
  /**
   * Wie het laatst wijzigde en wanneer (D-49).
   *
   * NWO beheert deze bibliotheek zelf, dus wie wat wanneer deed hoort erbij; dezelfde regel als
   * bij de conceptthistorie van D-45. Server-side gezet uit het token en nooit uit het lichaam
   * van het verzoek.
   */
  gewijzigdDoor: Person,
  gewijzigdOp: Moment,
})

/**
 * Elke regel hoort bij dít veld, en niet bij een sectie of bij iets anders.
 *
 * Twee dingen worden hier afgedwongen, en beide zijn nodig omdat een standaardveld één veld is en
 * geen sectie. Een sectieregel (zoals de waarschuwing over een opvallend korte sectie) heeft geen
 * `doelVeld` en hoort hier dus niet: bij het plaatsen zou hij naar niets wijzen. En een veldregel
 * met een ander doel dan dit veld of een van zijn rijvelden zou na het plaatsen naar het
 * bibliotheekveld blijven wijzen, dus naar een veld dat in de definitie niet bestaat.
 *
 * In het schema en niet bij het plaatsen, want daar is het te laat: dan is het standaardveld al
 * opgeslagen en levert elke plaatsing stil een regel minder op.
 *
 * Als losse functie en niet inline, want vier schema's hebben hem nodig: `Standaardveld` en
 * `Bibliotheekveld` hieronder, en de twee verzoekvormen van de store. Eén functie, dus geen
 * tweede idee over wanneer een regel bij een standaardveld hoort.
 *
 * **Geëxporteerd, en dat is nodig om een 500 te voorkomen.** Zonder deze controle op de
 * verzoekvorm neemt de store een standaardveld met een verkeerd gerichte regel aan, en valt hij
 * pas om bij het teruglezen van de rij die hij net zelf heeft geschreven. Dat is een 500 op een
 * verzoek dat de aanroeper verkeerd deed, en de aanroeper leest dan een melding waar hij niets
 * mee kan.
 */
export function rulesBelongToField(
  defaultField: { readonly veld: Field; readonly regels: readonly Rule[] },
  ctx: z.RefinementCtx,
): void {
  const own = new Set<string>([defaultField.veld.id])
  if (defaultField.veld.control === 'herhaalgroep') {
    for (const row of defaultField.veld.rijVelden) own.add(row.id)
  }
  defaultField.regels.forEach((rule, i) => {
    if (!('doelVeld' in rule)) {
      /**
       * Twee soorten vallen hier af, en om twee verschillende redenen. Een sectieregel draagt
       * `doelSectie` en gaat dus over iets wat een standaardveld niet is. Een
       * zichtbaarheidsregel draagt `doel` en hangt af van een ánder veld (R-33), en dat andere
       * veld staat niet in de bibliotheek: bij het plaatsen zou hij naar niets wijzen. Beide
       * krijgen hun eigen melding, want "gaat over een sectie" is voor de tweede onwaar.
       */
      ctx.addIssue({
        code: 'custom',
        path: ['regels', i],
        message:
          rule.soort === 'zichtbaarBij' || rule.soort === 'zichtbaarAls'
            ? 'een zichtbaarheidsregel hangt af van een ander veld, en een standaardveld staat op zichzelf'
            : `een regel van de soort ${rule.soort} gaat over een sectie en niet over een veld; een standaardveld is één veld`,
      })
      return
    }
    if (!own.has(rule.doelVeld)) {
      ctx.addIssue({
        code: 'custom',
        path: ['regels', i, 'doelVeld'],
        message: `regel ${rule.id} wijst naar ${rule.doelVeld} en dat is dit standaardveld niet`,
      })
    }
  })
}

/**
 * Het standaardveld zoals het in de bibliotheek staat en zoals het de definitie in gaat.
 *
 * Zonder `ingetrokken`: of een standaardveld nog wordt aangeboden is een stand van de
 * bibliotheek en geen eigenschap van het veld. Een geplaatste kopie draagt hem daarom nooit; zou
 * hij op de vorm staan, dan stond er in elke definitie een vlag die daar niets betekent en die
 * bij het intrekken van het bibliotheekveld in tegenspraak komt met D-51.
 */
export const DefaultField = Shape.superRefine(rulesBelongToField)
export type DefaultField = z.infer<typeof DefaultField>

/**
 * Hetzelfde standaardveld zoals de bibliotheek het teruggeeft, met of het is ingetrokken (D-49).
 *
 * Dit is de vorm die over de lijn gaat. De store houdt een ingetrokken veld staan, want het
 * gebruiksoverzicht van D-52 moet blijven werken, en het beheerscherm moet kunnen zien wat er is
 * weggehaald; dus staat er in elk antwoord of dat zo is. Wie de bibliotheek leest om er iets uit
 * te plaatsen wil juist de smalle vorm, en die is `Standaardveld`.
 *
 * **Waarom dit schema bestaat en niet een cast.** Tot M6.11 gaf de store `ingetrokken` mee en
 * parseerde de configurator het antwoord met het strikte `Standaardveld`, dus viel elke aanroep
 * om op een onbekende sleutel. Geen test zag dat: de storetests lazen het antwoord met een cast en
 * de configuratortests hadden een dubbel dat de vlag niet meestuurde. Twee halve waarheden over
 * dezelfde vorm, en dat is precies wat één schema voorkomt.
 */
export const LibraryField = Shape.extend({
  ingetrokken: z.boolean(),
  /**
   * Of er een goedgekeurde versie is (vier ogen, besluit van 1 november 2026).
   *
   * Een nieuw standaardveld is een voorstel tot een tweede beheerder het goedkeurt; tot dan staat het
   * niet in het palet en draagt deze vorm de inhoud van dat voorstel. Zonder deze vlag kan één persoon
   * een veld in elk nieuw formulier laten belanden, en dat is wat het vierogenprincipe uitsluit.
   */
  vrijgegeven: z.boolean(),
  /** De versie van een voorstel dat op beoordeling wacht, als er een is; hoogstens één tegelijk. */
  voorstel: z.number().int().min(1).optional(),
}).superRefine(rulesBelongToField)
export type LibraryField = z.infer<typeof LibraryField>

/**
 * Eén regel in het gebruiksoverzicht van een standaardveld (D-52).
 *
 * Afgeleid en nergens opgeslagen: het gebruik volgt uit de definities die het merk dragen, net
 * zoals de statuspil van D-26 volgt uit het bestaan van een publicatie en niet uit een kolom.
 * Twee plekken die hetzelfde beweren lopen uiteen, en dan is de vraag welke waar is.
 */
export const DefaultFieldUsage = z.strictObject({
  definitieId: z.string().min(1),
  /**
   * De titel en het bouwblok van het formulier, zodat het overzicht namen toont en geen kale id's
   * (besluit van 1 november 2026). De titel in de conceptvorm, want een concept mag nog zonder
   * Engelse titel zijn.
   */
  titel: TextDraft,
  blok: Block,
  veldId: FieldId,
  /** De versie waaruit dit veld is gekopieerd, zodat een oudere vorm opvalt. */
  versie: z.number().int().min(1),
  /** Of de definitie waarin het staat is gepubliceerd; dan is de kopie bevroren (R-99). */
  gepubliceerd: z.boolean(),
})
export type DefaultFieldUsage = z.infer<typeof DefaultFieldUsage>

/**
 * Hoe een versie van een standaardveld er nu voor staat (vier ogen, besluit van 1 november 2026).
 *
 * Afgeleid uit de beoordeling en nergens als kolom opgeslagen: een versie zonder beoordeling is een
 * voorstel, met beoordeling is ze goedgekeurd of afgewezen. Twee plekken die hetzelfde beweren lopen
 * uiteen.
 */
export const VersionState = z.enum(['voorstel', 'goedgekeurd', 'afgewezen'])
export type VersionState = z.infer<typeof VersionState>

/**
 * De wijzigingsnotitie bij een versie: waarom deze versie er is (besluit van 1 november 2026).
 *
 * Verplicht en niet leeg, want "Bijwerken naar versie 4" zonder reden laat de inrichter raden wat er
 * verandert. Gewone tekst en geen tweetalige `Tekst`: de notitie is voor de beheerders en inrichters van
 * NWO, niet voor de invuller.
 */
export const ChangeNote = z.string().trim().min(1, 'een wijzigingsnotitie is verplicht').max(500)

/** Het oordeel van de tweede beheerder over een voorstel, met de reden bij een afwijzing. */
export const VersionReview = z.strictObject({
  uitkomst: z.enum(['goedgekeurd', 'afgewezen']),
  door: Person,
  op: Moment,
  /** Verplicht bij een afwijzing (de reden), mag leeg bij een goedkeuring. */
  notitie: z.string().trim().max(500).optional(),
})
export type VersionReview = z.infer<typeof VersionReview>

/**
 * Eén versie van een standaardveld, zoals de geschiedenis haar bewaart (besluit van 1 november 2026).
 *
 * **Onveranderlijk.** Een nieuwe vorm is een nieuwe versie; de oude blijft staan zoals ze was, zodat
 * het gebruiksoverzicht en de inrichter kunnen zien wat er tussen versie 2 en 4 veranderde. De store
 * weigert in de database elke wijziging van een versierij.
 *
 * **Vier ogen.** Een versie begint als voorstel en wordt pas de huidige als een tweede persoon met
 * Bibliotheekbeheer haar goedkeurt; wie haar voorstelde kan dat niet zelf. Tot dan blijft de vorige
 * versie de huidige. Een afgewezen versie houdt haar nummer, zodat de geschiedenis volledig is.
 */
export const DefaultFieldVersion = z
  .strictObject({
    standaardveld: DefaultFieldId,
    versie: z.number().int().min(1),
    naam: Text,
    veld: Field,
    regels: z.array(Rule),
    adviesGeeftDoor: z.boolean().optional(),
    notitie: ChangeNote,
    voorgesteldDoor: Person,
    voorgesteldOp: Moment,
    stand: VersionState,
    beoordeling: VersionReview.optional(),
  })
  .superRefine(rulesBelongToField)
  .superRefine((version, ctx) => {
    if ((version.stand === 'voorstel') !== (version.beoordeling === undefined)) {
      ctx.addIssue({ code: 'custom', path: ['beoordeling'], message: 'een voorstel heeft geen beoordeling, een beoordeelde versie wel' })
    }
    if (version.beoordeling !== undefined && version.beoordeling.uitkomst !== version.stand) {
      ctx.addIssue({ code: 'custom', path: ['stand'], message: 'de stand volgt uit de uitkomst van de beoordeling' })
    }
  })
export type DefaultFieldVersion = z.infer<typeof DefaultFieldVersion>
